import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";

const films = [
  { id: "KTLcIqddWSE", title: "Emma & Mark's Wedding Film", venue: "Gaynes Park" },
  { id: "tSAyeoBfnOk", title: "Laura & Scott's Wedding Film", venue: "High House, Essex" },
  { id: "TSGJyA-meX8", title: "Tayla & Danny's Wedding Film", venue: "Hatfield Place" },
  { id: "OynwPvMB2i0", title: "Holly & Marc's Wedding Film", venue: "The Boundary" },
  { id: "1DXk4bMe3WM", title: "Leah & Josh's Wedding Film", venue: "Gaynes Park, Essex" },
  { id: "ylVnOyaa76E", title: "Bianca & Taylor's Wedding Film", venue: "Ye Olde Plough House" },
];

const photos = [
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/lgsxk5pq_Bonnie__Josh_SP_.jpg", alt: "Bonnie & Josh" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/2fbxfqwa_CHARLEY%20%26%20FABIO%20SP%2015-08-25%20%20040.jpg", alt: "Charley & Fabio" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/1qkcz52g_EMMA__MICHAEL_101023___471.jpg", alt: "Emma & Michael" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/49pnxgu0_JESS%20%26%20PETE%2022-05-25%20%20384.jpg", alt: "Jess & Pete" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/7x81ib5v_Oyin%20%26%20David%20Preview-24.jpg", alt: "Oyin & David" },
  { src: "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/4tj3d52l_Helen%20%26%20Daniel%2014-07-25%20%20009.jpg", alt: "Helen & Daniel" },
  { src: "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/7jvqirjn_JESS%20%26%20PETE%2022-05-25%20%20396.jpg", alt: "Jess & Pete" },
  { src: "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/aiky4ekf_Paige__Richard_070724__625.jpg", alt: "Paige & Richard" },
  { src: "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/tohymtf9_ROBYN%20%26%20CHARLIE%2021-08-25%20044.jpg", alt: "Robyn & Charlie" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/3xdtp1cg_Preview-45.jpg", alt: "Essex Wedding Preview" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/ttll3q8t_Paige__Richard_070724__701.jpg", alt: "Paige & Richard" },
  { src: "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/52g6cs4z_London%20Wedding%20Previews-21.jpg", alt: "London Wedding" },
];

export default function FeaturedFilms() {
  const [activeVideo, setActiveVideo] = useState(null);
  const [tab, setTab] = useState("films");
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  return (
    <section
      id="films"
      data-testid="featured-films-section"
      className="bg-[#0A0908] py-24 md:py-32"
      aria-label="Wedding films and photography portfolio"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Our work
          </p>
          <h2
            data-testid="films-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0]"
          >
            Real weddings. Real love. Real tears.
          </h2>
          <p className="mt-4 text-base text-[#A19D94] font-sans leading-relaxed max-w-xl mx-auto">
            Every film is different. Because every couple is.
          </p>
        </motion.div>

        {/* Toggle */}
        <div className="flex justify-center mt-10">
          <div
            data-testid="films-photo-toggle"
            className="inline-flex rounded-full bg-white/10 border border-white/20 p-1"
          >
            <button
              onClick={() => setTab("films")}
              data-testid="toggle-films"
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                tab === "films"
                  ? "bg-[#C2A878] text-[#0A0908]"
                  : "text-white/60 hover:text-white/80"
              }`}
            >
              Wedding Films
            </button>
            <button
              onClick={() => setTab("photos")}
              data-testid="toggle-photos"
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                tab === "photos"
                  ? "bg-[#C2A878] text-[#0A0908]"
                  : "text-white/60 hover:text-white/80"
              }`}
            >
              Photography
            </button>
          </div>
        </div>

        {/* Films Grid */}
        <AnimatePresence mode="wait">
          {tab === "films" && (
            <motion.div
              key="films"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {films.map((film, i) => (
                <motion.button
                  key={film.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  onClick={() => setActiveVideo(film.id)}
                  data-testid={`film-card-${i}`}
                  className="group relative aspect-video overflow-hidden bg-[#11100F] text-left"
                >
                  <img
                    src={`https://img.youtube.com/vi/${film.id}/maxresdefault.jpg`}
                    alt={`${film.title} - ${film.venue}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="play-button-overlay">
                    <Play className="text-white drop-shadow-lg" fill="white" strokeWidth={0} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="font-serif text-lg text-[#F5F5F0] leading-tight">{film.title}</p>
                    <p className="text-xs text-[#A19D94] font-sans mt-1">{film.venue}</p>
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}

          {/* Photography Grid */}
          {tab === "photos" && (
            <motion.div
              key="photos"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3"
            >
              {photos.map((photo, i) => (
                <motion.button
                  key={photo.src}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  onClick={() => setLightboxPhoto(photo)}
                  data-testid={`photo-card-${i}`}
                  className="group relative aspect-[3/4] overflow-hidden bg-[#11100F]"
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="video-modal-overlay"
          data-testid="video-modal"
          onClick={() => setActiveVideo(null)}
        >
          <button
            onClick={() => setActiveVideo(null)}
            data-testid="video-modal-close"
            className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
          >
            <X size={32} />
          </button>
          <div
            className="w-full max-w-4xl aspect-video mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
              title="Wedding Film"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div
          className="video-modal-overlay"
          data-testid="photo-lightbox"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
          >
            <X size={32} />
          </button>
          <div
            className="max-w-5xl max-h-[85vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxPhoto.src}
              alt={lightboxPhoto.alt}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
        </div>
      )}
    </section>
  );
}
